// src/components/MissingInvoicesBox.js — Dashboard checkpoint: MNG invoice numbers missing from Invoice Details.
// Computed live on the server from the `invoices` table (QR + Marg together). A number leaves the box by itself the
// moment a bill with that number appears. Owner / team leader can dismiss a number with a note (e.g. skipped by Marg).
import React, { useState, useEffect, useCallback } from 'react';
import { get, post, getUser } from '../utils/api';

function ago(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' min';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + ' h ' + (mins % 60) + ' min';
  return Math.floor(h / 24) + ' d ' + (h % 24) + ' h';
}

export default function MissingInvoicesBox({ card, tone }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [dismissing, setDismissing] = useState(null);   // the row being dismissed
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const canDismiss = ['owner', 'team_leader'].includes((getUser() || {}).role);

  const load = useCallback(async () => {
    try { setData(await get('/api/dashboard/missing-invoices')); setHidden(false); }
    catch (e) { if (/denied|permission/i.test(e.message || '')) setHidden(true); else console.error(e); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(iv);
  }, [load]);

  async function dismiss() {
    setErr('');
    try { await post('/api/dashboard/missing-invoices/dismiss', { num: dismissing.num, note }); setDismissing(null); setNote(''); load(); }
    catch (e) { setErr(e.message); }
  }

  if (hidden || !data) return null;
  const count = data.total;

  return (
    <section className={card + ' p-5 flex flex-col gap-3'}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Missing invoice numbers</h2>
          <span className={'text-xs font-semibold px-2.5 py-1 rounded tabular-nums ' + (count === 0 ? tone.ok : tone.amber)}>
            {count === 0 ? 'CLEAR' : count + ' MISSING'}
          </span>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">In the MNG series but not in Invoice Details yet. Each leaves this box when it arrives (QR or Marg).</span>
      </div>

      {count === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No gaps in the MNG series over the last {data.days} days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-2 pr-3 font-semibold">Invoice</th>
              <th className="py-2 px-3 font-semibold">Missing for</th>
              {canDismiss && <th className="py-2 pl-3 font-semibold text-right"></th>}
            </tr></thead>
            <tbody>
              {data.missing.map(r => (
                <tr key={r.num} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-2.5 pr-3 font-semibold tabular-nums text-gray-800 dark:text-gray-100">{r.number}</td>
                  <td className="py-2.5 px-3 tabular-nums">{ago(r.since)}</td>
                  {canDismiss && (
                    <td className="py-2.5 pl-3 text-right">
                      <button onClick={() => { setDismissing(r); setNote(''); setErr(''); }}
                        className="text-xs px-2 py-1 rounded font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300">
                        Not a problem
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {count > data.missing.length && (
        <p className="text-xs text-gray-500 dark:text-gray-400">Showing the highest {data.missing.length} of {count}.</p>
      )}

      {dismissing && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
          <span className="text-sm font-semibold">{dismissing.number}:</span>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why is this not a problem? (e.g. cancelled in Marg)"
            className="flex-1 min-w-[220px] border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg px-2 py-1.5 text-sm" />
          <button onClick={dismiss} disabled={!note.trim()} className="px-3 py-1.5 bg-brand-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">Save</button>
          <button onClick={() => setDismissing(null)} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm">Cancel</button>
          {err && <span className="text-sm text-red-600">{err}</span>}
        </div>
      )}
    </section>
  );
}
